import {
  db,
  type CargoLocal,
  type CitaLocal,
  type CitaTramoLocal,
  type ClienteLocal,
  type RecursoLocal,
} from './db.js'
import { diaLocal, limitesDelDia } from './formato.js'

/**
 * Las consultas que alimentan la UI. Todas leen de Dexie y ninguna toca la red.
 *
 * Se usan con `useLiveQuery`, así que se vuelven a correr solas cuando el
 * sincronizador escribe. Esa es la razón por la que la UI no necesita saber que
 * existe una red: los datos cambian abajo y la pantalla se repinta.
 */

export interface TramoConCita {
  tramo: CitaTramoLocal
  cita: CitaLocal
  /** La ficha basica del canal operativo. La extendida se carga aparte (§5.5). */
  cliente: ClienteLocal | null
  /** Los recursos que este tramo ocupa. Puede estar vacío (§4.4). */
  recursos: string[]
}

/** La agenda de un día, ya resuelta: tramos con su cita, su cliente y sus recursos. */
export async function agendaDelDia(dia: string): Promise<TramoConCita[]> {
  const { inicio, fin } = limitesDelDia(dia)

  // El índice sobre `inicio` es lo que hace que esto no recorra la tabla
  // entera en cada repintado.
  const tramos = await db.cita_tramo.where('inicio').between(inicio, fin, true, false).toArray()
  if (tramos.length === 0) return []

  const citas = await db.cita.bulkGet([...new Set(tramos.map((t) => t.cita_id))])
  const porCita = new Map(citas.filter(Boolean).map((c) => [c!.id, c!]))

  const clientes = await db.cliente.bulkGet([
    ...new Set([...porCita.values()].map((c) => c.cliente_id)),
  ])
  const porCliente = new Map(clientes.filter(Boolean).map((c) => [c!.id, c!]))

  const reservas = await db.reserva_recurso
    .where('cita_tramo_id')
    .anyOf(tramos.map((t) => t.id))
    .toArray()

  const recursosPorTramo = new Map<string, string[]>()
  for (const r of reservas) {
    if (!r.activa) continue
    const lista = recursosPorTramo.get(r.cita_tramo_id) ?? []
    lista.push(r.recurso_id)
    recursosPorTramo.set(r.cita_tramo_id, lista)
  }

  const salida: TramoConCita[] = []
  for (const tramo of tramos) {
    const cita = porCita.get(tramo.cita_id)
    if (!cita) continue
    // Las citas terminales no ocupan la grilla: su hueco ya volvió al pool.
    if (['cancelada', 'no_asistio', 'pendiente_reprogramar'].includes(cita.estado)) continue

    salida.push({
      tramo,
      cita,
      cliente: porCliente.get(cita.cliente_id) ?? null,
      // Un tramo sin recursos es legítimo: el paciente esperando que actúe la
      // anestesia. Se dibuja como hueco de la cita, no se descarta.
      recursos: recursosPorTramo.get(tramo.id) ?? [],
    })
  }

  return salida.sort((a, b) => a.tramo.inicio.localeCompare(b.tramo.inicio))
}

/** Los recursos que se muestran como columnas, ordenados y agrupables. */
export async function recursosVisibles(): Promise<
  (RecursoLocal & { esPersona: boolean; tipoNombre: string; bufferMin: number })[]
> {
  const recursos = await db.recurso.filter((r) => r.activo).toArray()
  const tipos = await db.recurso_tipo.toArray()
  const porTipo = new Map(tipos.map((t) => [t.id, t]))

  return recursos
    .map((r) => {
      const tipo = porTipo.get(r.recurso_tipo_id)
      return {
        ...r,
        esPersona: tipo?.es_persona ?? false,
        tipoNombre: tipo?.nombre ?? '',
        bufferMin: tipo?.buffer_min ?? 0,
      }
    })
    .sort((a, b) => {
      // Personas primero: es como piensa la recepción, que agenda "con la
      // doctora" y después ve en qué consultorio cae.
      if (a.esPersona !== b.esPersona) return a.esPersona ? -1 : 1
      return a.nombre.localeCompare(b.nombre)
    })
}

/**
 * El rango horario que de verdad se trabaja ese día.
 *
 * Sin esto el timeline dibuja una franja fija —07:00 a 21:00— y en una clínica
 * que abre de 8 a 18 eso es un tercio de la pantalla mostrando horas en las que
 * no hay nadie. El eje tiene que salir de los horarios cargados, no de una
 * constante.
 */
export async function rangoDelDia(
  dia: string,
  recursoIds: string[],
): Promise<{ desde: number; hasta: number }> {
  // `dia` viene como YYYY-MM-DD en hora local; el día de semana que necesita
  // `recurso_horario` sigue la convención de Postgres: 0 = domingo.
  const diaSemana = new Date(`${dia}T12:00:00`).getDay()

  const horarios = await db.recurso_horario
    .where('dia_semana')
    .equals(diaSemana)
    .toArray()

  const propios = horarios.filter((h) => recursoIds.includes(h.recurso_id))

  if (propios.length === 0) return { desde: 8, hasta: 20 }

  const aHora = (hhmm: string) => Number(hhmm.slice(0, 2))
  const desde = Math.min(...propios.map((h) => aHora(h.desde)))
  const hasta = Math.max(...propios.map((h) => Math.ceil(Number(h.hasta.slice(0, 2)) + (Number(h.hasta.slice(3, 5)) > 0 ? 1 : 0))))

  // Arranca en la hora de apertura, sin aire arriba: una franja vacía antes de
  // que abra la clínica es lo primero que se ve al entrar y no dice nada. Abajo
  // sí queda una hora, para las citas que se estiran pasado el cierre.
  return { desde, hasta: Math.min(24, hasta + 1) }
}

/** El día de un profesional: su lista, no la grilla completa (§7.1). */
export async function miDia(recursoPersonaId: string, dia: string): Promise<TramoConCita[]> {
  const todo = await agendaDelDia(dia)
  return todo.filter((t) => t.recursos.includes(recursoPersonaId))
}

export async function buscarClientes(texto: string, limite = 20) {
  const aguja = texto.trim().toLowerCase()
  if (aguja.length < 2) return []

  return db.cliente
    .filter(
      (c) =>
        c.nombre.toLowerCase().includes(aguja) ||
        (c.telefono ?? '').includes(aguja) ||
        (c.doc ?? '').includes(aguja),
    )
    .limit(limite)
    .toArray()
}

export async function serviciosActivos() {
  return db.servicio.filter((s) => s.activo).toArray()
}

export async function conflictosAbiertos() {
  return db.conflictos.filter((c) => !c.resuelto).reverse().sortBy('detectadoEn')
}

/** Las solicitudes de la reserva pública esperando confirmación (T11). */
export async function solicitudesPendientes(): Promise<TramoConCita[]> {
  const citas = await db.cita
    .filter((c) => c.origen === 'publico' && c.estado === 'pendiente')
    .toArray()
  if (citas.length === 0) return []

  const tramos = await db.cita_tramo.where('cita_id').anyOf(citas.map((c) => c.id)).toArray()
  const clientes = await db.cliente.bulkGet([...new Set(citas.map((c) => c.cliente_id))])
  const porCliente = new Map(clientes.filter(Boolean).map((c) => [c!.id, c!]))
  const porCita = new Map(citas.map((c) => [c.id, c]))

  return tramos
    .filter((t) => t.orden === 0)
    .map((tramo) => {
      const cita = porCita.get(tramo.cita_id)!
      return {
        tramo,
        cita,
        cliente: porCliente.get(cita.cliente_id) ?? null,
        recursos: [],
      }
    })
    .sort((a, b) => a.tramo.inicio.localeCompare(b.tramo.inicio))
}

/** El día local `YYYY-MM-DD` del primer tramo de una cita, o `null` si no está en Dexie. */
export async function diaDeCita(citaId: string): Promise<string | null> {
  const tramos = await db.cita_tramo.where('cita_id').equals(citaId).sortBy('orden')
  if (tramos.length === 0) return null
  return diaLocal(tramos[0]!.inicio)
}

// ── El día de la clínica ────────────────────────────────────────────────────

export type ColumnaHoy = 'esperando' | 'en_sillon' | 'para_cobrar' | 'agendada' | 'cerrada'

export interface FilaHoy {
  cita: CitaLocal
  tramo: CitaTramoLocal
  cliente: ClienteLocal | null
  /** El nombre del profesional del tramo, resuelto por el tipo de recurso. */
  profesional: string | null
  /** El consultorio, si el tramo ocupa uno. */
  espacio: string | null
  columna: ColumnaHoy
  /** Saldo impago de la cita, en guaraníes. */
  saldo: number
  /** Minutos que lleva esperando, si `llegada_at` está puesto. */
  esperandoMin: number | null
}

/**
 * El día de la clínica, clasificado por dónde está cada persona.
 *
 * El orden de la clasificación importa: la primera condición que se cumple
 * gana. Alguien en el sillón está en el sillón aunque haya llegado hace una
 * hora, y alguien atendido con saldo está para cobrar aunque su estado siga
 * diciendo que llegó.
 *
 * Sólo Dexie: es la pantalla que tiene que funcionar cuando se cae internet a
 * las ocho de la mañana.
 */
export async function elDia(dia: string): Promise<FilaHoy[]> {
  const tramos = await agendaDelDia(dia)
  if (tramos.length === 0) return []

  // Una cita con varios tramos aparece una sola vez, con el primero.
  const primeros = new Map<string, TramoConCita>()
  for (const t of tramos) {
    const previo = primeros.get(t.cita.id)
    if (!previo || t.tramo.inicio < previo.tramo.inicio) primeros.set(t.cita.id, t)
  }

  const ids = [...primeros.keys()]
  const cargos = await db.cargo.filter((g) => ids.includes(g.cita_id)).toArray()
  const pagos = cargos.length
    ? await db.pago.where('cargo_id').anyOf(cargos.map((g) => g.id)).toArray()
    : []

  const saldoPorCita = new Map<string, number>()
  for (const cargo of cargos) {
    const pagado = pagos
      .filter((p) => p.cargo_id === cargo.id)
      .reduce((a, p) => a + p.monto, 0)
    saldoPorCita.set(cargo.cita_id, (saldoPorCita.get(cargo.cita_id) ?? 0) + (cargo.monto - pagado))
  }

  const recursos = await db.recurso.bulkGet([
    ...new Set([...primeros.values()].flatMap((t) => t.recursos)),
  ])
  const porRecurso = new Map(recursos.filter(Boolean).map((r) => [r!.id, r!]))

  // Quién es persona y quién es espacio lo dice el tipo, no el recurso: la
  // fila muestra «Dra. Benítez», no la lista cruda de lo que ocupa el tramo.
  const tipos = await db.recurso_tipo.toArray()
  const esPersona = new Map(tipos.map((t) => [t.id, t.es_persona]))

  const ahora = Date.now()

  const filas = [...primeros.values()].map(({ cita, tramo, cliente, recursos: ids }) => {
    const suyos = ids.map((id) => porRecurso.get(id)).filter((r) => r !== undefined)
    const persona = suyos.find((r) => esPersona.get(r.recurso_tipo_id) === true)
    const espacio = suyos.find((r) => esPersona.get(r.recurso_tipo_id) === false)
    const saldo = Math.max(0, saldoPorCita.get(cita.id) ?? 0)
    const llegada = cita.llegada_at ?? null

    const columna: ColumnaHoy =
      cita.estado === 'en_atencion'
        ? 'en_sillon'
        : cita.estado === 'atendida' && saldo > 0
          ? 'para_cobrar'
          : ['facturada', 'atendida', 'no_asistio', 'cancelada'].includes(cita.estado)
            ? 'cerrada'
            : llegada
              ? 'esperando'
              : 'agendada'

    return {
      cita,
      tramo,
      cliente,
      profesional: persona?.nombre ?? null,
      espacio: espacio?.nombre ?? null,
      columna,
      saldo,
      esperandoMin:
        llegada && columna === 'esperando'
          ? Math.max(0, Math.round((ahora - new Date(llegada).getTime()) / 60000))
          : null,
    }
  })

  return filas.sort((a, b) => a.tramo.inicio.localeCompare(b.tramo.inicio))
}

/** Las citas futuras de un paciente que hay en Dexie. Sirve para saber si tiene control. */
export async function tieneProximaCita(clienteId: string, desde = new Date()): Promise<boolean> {
  const citas = await db.cita
    .where('cliente_id')
    .equals(clienteId)
    .filter((c) => !['cancelada', 'no_asistio'].includes(c.estado))
    .toArray()
  if (citas.length === 0) return false

  const ids = citas.map((c) => c.id)
  const tramos = await db.cita_tramo.where('cita_id').anyOf(ids).toArray()
  const corte = desde.toISOString()
  return tramos.some((t) => t.inicio > corte)
}

// ── El paciente: su cuenta y sus citas ──────────────────────────────────────

/** Un cargo del paciente con lo pagado y de qué cita salió. */
export interface CargoDePaciente {
  cargo: CargoLocal
  pagado: number
  saldo: number
  citaId: string
  /** Inicio del primer tramo de la cita, o `null` si no está en la ventana. */
  cuando: string | null
}

/**
 * La cuenta del paciente: todos sus cargos, del más nuevo al más viejo.
 *
 * Sólo Dexie: es pantalla de mostrador y tiene que funcionar sin red. Un cargo
 * puede sobrevivir a su cita —la deuda vieja se cobra aunque el tramo ya no esté
 * en la ventana—, por eso la fecha es opcional.
 */
export async function cuentaDelCliente(clienteId: string): Promise<CargoDePaciente[]> {
  const citas = await db.cita.where('cliente_id').equals(clienteId).toArray()
  const ids = citas.map((c) => c.id)
  const cargos = await db.cargo.filter((g) => ids.includes(g.cita_id)).toArray()
  if (cargos.length === 0) return []

  const pagos = await db.pago.where('cargo_id').anyOf(cargos.map((g) => g.id)).toArray()
  const pagadoPorCargo = new Map<string, number>()
  for (const p of pagos) {
    pagadoPorCargo.set(p.cargo_id, (pagadoPorCargo.get(p.cargo_id) ?? 0) + p.monto)
  }

  // El día del cargo es el del primer tramo de su cita. Con `anyOf` al revés de
  // lo que haría un joiner ingenuo: una cita sin tramos en la ventana sigue
  // debiendo, y la fila se muestra «sin fecha».
  const tramos = await db.cita_tramo.where('cita_id').anyOf(ids).toArray()
  const inicioPorCita = new Map<string, string>()
  for (const t of tramos) {
    const previo = inicioPorCita.get(t.cita_id)
    if (!previo || t.inicio < previo) inicioPorCita.set(t.cita_id, t.inicio)
  }

  // El id es UUIDv7, que ordena por tiempo: mayor id, cargo más nuevo.
  return cargos
    .map((g) => {
      const pagado = pagadoPorCargo.get(g.id) ?? 0
      return {
        cargo: g,
        pagado,
        saldo: Math.max(0, g.monto - pagado),
        citaId: g.cita_id,
        cuando: inicioPorCita.get(g.cita_id) ?? null,
      }
    })
    .sort((a, b) => b.cargo.id.localeCompare(a.cargo.id))
}

/**
 * Las citas del paciente que hay en Dexie, de la más próxima a la más vieja.
 *
 * Es la ventana operativa que este dispositivo tiene bajada, no el historial
 * completo: una cita vieja que ya no está en la ventana no aparece, y la
 * pantalla lo dice sin mentirle al mostrador.
 */
export async function citasDelCliente(clienteId: string): Promise<TramoConCita[]> {
  const citas = await db.cita.where('cliente_id').equals(clienteId).toArray()
  if (citas.length === 0) return []

  const porCita = new Map(citas.map((c) => [c.id, c]))
  const tramos = await db.cita_tramo.where('cita_id').anyOf([...porCita.keys()]).toArray()
  if (tramos.length === 0) return []

  const cliente = await db.cliente.get(clienteId)

  const reservas = await db.reserva_recurso
    .where('cita_tramo_id')
    .anyOf(tramos.map((t) => t.id))
    .toArray()

  const recursosPorTramo = new Map<string, string[]>()
  for (const r of reservas) {
    if (!r.activa) continue
    const lista = recursosPorTramo.get(r.cita_tramo_id) ?? []
    lista.push(r.recurso_id)
    recursosPorTramo.set(r.cita_tramo_id, lista)
  }

  const ahora = new Date().toISOString()
  const salida: TramoConCita[] = []
  for (const tramo of tramos) {
    const cita = porCita.get(tramo.cita_id)
    if (!cita) continue
    salida.push({
      tramo,
      cita,
      cliente: cliente ?? null,
      recursos: recursosPorTramo.get(tramo.id) ?? [],
    })
  }

  // Del futuro hacia el pasado: las próximas, de la más cercana hacia adelante;
  // las anteriores, de la más reciente hacia atrás. La pantalla corta en el
  // instante actual y se apoya en que el orden ya viene resuelto.
  return salida.sort((a, b) => {
    const aFutura = a.tramo.inicio > ahora
    if (aFutura !== (b.tramo.inicio > ahora)) return aFutura ? -1 : 1
    return aFutura
      ? a.tramo.inicio.localeCompare(b.tramo.inicio)
      : b.tramo.inicio.localeCompare(a.tramo.inicio)
  })
}
